// src/modules/app-views/_apps/appsindico/units/pages/Document/tabs/BicyclesTab.jsx
// *******************************************************************************
// BICYCLES TAB
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import {
  Table,
  Button,
  Flex,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const RIM_OPTIONS = ['12', '16', '20', '24', '26', '27.5', '29'].map(r => ({
  value: r,
  label: r + '"',
}));

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const BicyclesTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, _id, refresh } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bicycles, setBicycles] = useState([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!data?.bicycleIds?.length) {
        setBicycles([]);
        return;
      }
      const promises = data.bicycleIds.map(id =>
        axiosService.get({ url: `/v1/bicycles/${id}` }).catch(() => [null])
      );
      const results = await Promise.all(promises);
      setBicycles(results.map(r => r?.[0]).filter(Boolean));
    };
    load();
  }, [data?.bicycleIds]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(() => {
    form.resetFields();
    setShowModal(true);
  }, [form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const [bicycle] = await axiosService.post({
        url: '/v1/bicycles',
        data: { ...values, unitId: _id },
      });

      if (bicycle?._id) {
        const updatedIds = [...(data?.bicycleIds || []), bicycle._id];
        await axiosService.put({
          url: `/v1/units/${_id}`,
          data: { bicycleIds: updatedIds },
        });
        refresh?.();
        setShowModal(false);
        appService.message({ flag: 's', text: 'saved_successfully' });
      }
    } catch (error) {
      if (!error?.errorFields) {
        appService.message({ flag: 'e', text: 'error_saving' });
      }
    } finally {
      setLoading(false);
    }
  }, [form, data?.bicycleIds, _id, refresh]);

  const handleRemove = useCallback(
    async bicycleId => {
      setLoading(true);
      try {
        const updatedIds = (data?.bicycleIds || []).filter(id => id !== bicycleId);
        await axiosService.put({
          url: `/v1/units/${_id}`,
          data: { bicycleIds: updatedIds },
        });
        await axiosService.delete({ url: '/v1/bicycles', _id: bicycleId });
        refresh?.();
        appService.message({ flag: 's', text: 'removed_successfully' });
      } catch {
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [data?.bicycleIds, _id, refresh]
  );

  const handleModalCancel = useCallback(() => {
    setShowModal(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('brand'),
      dataIndex: ['brand'],
      render: text => text || '-',
    },
    {
      title: translateX('model'),
      dataIndex: ['model'],
      render: text => text || '-',
    },
    {
      title: translateX('color'),
      dataIndex: ['color'],
      width: 140,
      render: text => text || '-',
    },
    {
      title: translateX('rim'),
      dataIndex: ['rim'],
      width: 100,
      render: text => (text ? text + '"' : '-'),
    },
    {
      title: translateX('actions'),
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Popconfirm title={translateX('confirm_delete')} onConfirm={() => handleRemove(record._id)}>
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={loading}
          />
        </Popconfirm>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={bicycles}
        pagination={false}
        loading={loading}
        size="small"
      />

      <Flex justify="end" className="mt-3">
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_bicycle')}
        </Button>
      </Flex>

      <Modal
        title={translateX('add_bicycle')}
        open={showModal}
        onCancel={handleModalCancel}
        onOk={handleSave}
        confirmLoading={loading}
        destroyOnHidden
        width={520}
      >
        <Form form={form} layout="vertical" className="mt-3">
          <Flex gap={12}>
            <Form.Item
              name="brand"
              label={translateX('brand')}
              style={{ flex: 1 }}
            >
              <Input placeholder={translateX('brand')} />
            </Form.Item>
            <Form.Item
              name="model"
              label={translateX('model')}
              style={{ flex: 1 }}
            >
              <Input placeholder={translateX('model')} />
            </Form.Item>
          </Flex>

          <Flex gap={12}>
            <Form.Item
              name="color"
              label={translateX('color')}
              style={{ flex: 1 }}
            >
              <Input placeholder={translateX('color')} />
            </Form.Item>
            <Form.Item
              name="rim"
              label={translateX('rim')}
              style={{ width: 140 }}
            >
              <Select
                options={RIM_OPTIONS}
                placeholder={translateX('select')}
                allowClear
              />
            </Form.Item>
          </Flex>

          <Form.Item name="serialNumber" label={translateX('serial_number')}>
            <Input placeholder={translateX('serial_number')} />
          </Form.Item>

          <Form.Item name="value" label={translateX('estimated_value')}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              placeholder="0,00"
            />
          </Form.Item>

          <Form.Item name="observation" label={translateX('observation')}>
            <Input.TextArea rows={3} placeholder={translateX('observation')} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default BicyclesTab;
